import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If token expired, try to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${API_URL}/api/users/auth/refresh/`, {
            refresh: refreshToken,
          });
          
          const { access } = response.data;
          localStorage.setItem('access_token', access);
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// API Functions
export const registerAthlete = async (userData) => {
  const response = await apiClient.post('/users/register/athlete/', userData);
  return response.data;
};

export const registerCoach = async (userData) => {
  const response = await apiClient.post('/users/register/coach/', userData);
  return response.data;
};

export const completeAthleteOnboarding = async (onboardingData) => {
  // Use FormData for multipart upload
  const formData = onboardingData instanceof FormData ? onboardingData : new FormData();
  
  // If onboardingData is not FormData, convert it
  if (!(onboardingData instanceof FormData)) {
    Object.keys(onboardingData).forEach(key => {
      const value = onboardingData[key];
      if (Array.isArray(value)) {
        // For arrays like preferred_workout_days
        value.forEach(item => formData.append(key, item));
      } else if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });
  }
  
  // Use the new profile endpoint with multipart/form-data
  const response = await apiClient.post('/profiles/athlete/onboarding/complete/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const login = async (credentials) => {
  const response = await apiClient.post('/users/auth/login/', credentials);
  return response.data;
};

export const refreshToken = async (refreshToken) => {
  const response = await axios.post(`${API_URL}/api/users/auth/refresh/`, {
    refresh: refreshToken,
  });
  return response.data;
};

export const getCurrentUser = async () => {
  const response = await apiClient.get('/users/me/');
  return response.data;
};

// Profile-specific endpoints
export const getAthleteProfile = async () => {
  const response = await apiClient.get('/profiles/athlete/profile/');
  return response.data;
};

export const updateAthleteProfile = async (profileData) => {
  const formData = new FormData();
  Object.keys(profileData).forEach(key => {
    const value = profileData[key];
    if (value !== null && value !== undefined) {
      formData.append(key, value);
    }
  });
  
  const response = await apiClient.patch('/profiles/athlete/profile/update/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const uploadProfilePhoto = async (imageFile) => {
  const formData = new FormData();
  formData.append('profile_image', imageFile);
  
  const response = await apiClient.post('/profiles/athlete/profile/upload/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// For backward compatibility and alternative usage
export const completeOnboarding = completeAthleteOnboarding;

export default apiClient;